import { IntegrationsService, PromptColumnsService } from '@/api/';
import { InputWithButton } from '@/components/Inputwithbutton'; // Import the new component
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useStore } from '@nanostores/react';
import { CalendarIcon } from '@radix-ui/react-icons';
import { useQuery } from '@tanstack/react-query';
import {
  addDays,
  format,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import AgentsSelector from './AgentsSelector';

const formSchema = z
  .object({
    projectName: z.string().min(2, {
      message: 'Project name must be at least 2 characters.',
    }),
    sourceType: z.enum(['csv', 'zendesk', 'audiofile', 'aircall']), // Added 'audiofile' to enum
    projectFile: z
      .union([
        z.instanceof(File, { message: 'Select a file to process.' }),
        z.array(z.instanceof(File, { message: 'Each item must be a file.' })),
      ])
      .optional()
      .refine(
        files => {
          if (Array.isArray(files)) {
            return files.length > 0;
          }
          return true; // If it's a single file, it's valid
        },
        {
          message: 'Select at least one file to process.',
        },
      ),
    agents: z.object({}),
    is_external_data_source_syncing_status_email_notification_enabled:
      z.boolean(),
    is_processor_status_email_notification_enabled: z.boolean(),
    auto_run_analyzer_after_external_data_source_syncing_completed: z.boolean(),
  })
  .superRefine((data, ctx) => {
    // Validate the projectFile based on sourceType
    if (data.sourceType === 'csv' && !data.projectFile) {
      ctx.addIssue({
        message: 'Select a CSV file to process.',
        path: ['projectFile'],
        code: z.ZodIssueCode.custom,
      });
    } else if (
      data.sourceType === 'audiofile' &&
      (!data.projectFile ||
        (Array.isArray(data.projectFile)
          ? !data.projectFile.every(file =>
            ['audio/mpeg', 'audio/wav', 'audio/x-m4a'].includes(file.type),
          )
          : !['audio/mpeg', 'audio/wav', 'audio/x-m4a'].includes(
            data.projectFile.type,
          )))
    ) {
      ctx.addIssue({
        message: 'Select an MP3, WAV, or M4A file to process.',
        path: ['projectFile'],
        code: z.ZodIssueCode.custom,
      });
    }
  });

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function NewProjectDrawer({ open, setOpen }: Props) {
  const org = useStore($org);
  const ws = useStore($workspace);
  const [sourceType, setSourceType] = useState<
    'csv' | 'zendesk' | 'audiofile' | 'aircall'
  >('csv'); // Updated state type

  const activeIntegrations = useQuery({
    queryKey: ['getOrgIntegrations', org?.id],
    queryFn: async () => {
      if (!org) return;
      const res = await IntegrationsService.getOrgIntegrations(org?.id);
      return res;
    },
  });

  const [selectedAgents, setSelectedAgents] = useState<{
    [key: string]: boolean;
  }>({});

  const promtColumns = useQuery({
    queryKey: ['promptsColumns', org?.id],
    queryFn: async () => {
      if (org?.id) {
        const res = await PromptColumnsService.getPromptColumns(org?.id || '');
        return res;
      }
      return [];
    },
  });

  const [date, setDate] = React.useState<DateRange | undefined>({
    from: addDays(new Date(), -7),
    to: new Date(),
  });

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      projectName: '',
      agents: {},
      sourceType: 'csv',
      is_external_data_source_syncing_status_email_notification_enabled: true,
      is_processor_status_email_notification_enabled: true,
      auto_run_analyzer_after_external_data_source_syncing_completed: true,
    },
  });

  // 2. Define a submit handler.
  const [isLoading, setIsLoading] = useState(false);
  async function onSubmit(values: z.infer<typeof formSchema>) {
    // Do something with the form values.
    if (Object.keys(selectedAgents).length === 0) {
      toast('Please select at least one agent!');
    } else {
      setIsLoading(true);
      let agentsArray = [];
      for (let key in selectedAgents) {
        if (selectedAgents[key] === true) {
          const foundPrompt = promtColumns?.data?.find(dat => dat.id === key);
          if (foundPrompt) {
            agentsArray.push(key);
          }
        }
      }
      if (values.sourceType === 'zendesk') {
        // call the api to create project with zendesk
        // createProjectWithExternalDataSource
        await createProjectWithExternalDataSource(
          values.projectName,
          org?.id ?? '',
          ws?.workspace.id ?? '',
          agentsArray,
          'Zendesk',
          date?.from?.toISOString() as string,
          date?.to?.toISOString() as string,
          {
            is_external_data_source_syncing_status_email_notification_enabled:
              values.is_external_data_source_syncing_status_email_notification_enabled,
            is_processor_status_email_notification_enabled:
              values.is_processor_status_email_notification_enabled,
            auto_run_analyzer_after_external_data_source_syncing_completed:
              values.auto_run_analyzer_after_external_data_source_syncing_completed,
          },
        );
      } else if (values.sourceType === 'aircall') {
        // call the api to create project with zendesk
        // createProjectWithExternalDataSource
        await createProjectWithExternalDataSource(
          values.projectName,
          org?.id ?? '',
          ws?.workspace.id ?? '',
          agentsArray,
          'Aircall',
          date?.from?.toISOString() as string,
          date?.to?.toISOString() as string,
          {
            is_external_data_source_syncing_status_email_notification_enabled:
              values.is_external_data_source_syncing_status_email_notification_enabled,
            is_processor_status_email_notification_enabled:
              values.is_processor_status_email_notification_enabled,
            auto_run_analyzer_after_external_data_source_syncing_completed:
              values.auto_run_analyzer_after_external_data_source_syncing_completed,
          },
        );
      } else if (values.sourceType === 'audiofile') {
        // Handle audio file processing
        console.log('values.projectFile', values.projectFile);
        await createProjectWithAudioFiles(
          values.projectName,
          org?.id ?? '',
          ws?.workspace.id ?? '',
          values.projectFile as File[],
          agentsArray,
          {
            is_external_data_source_syncing_status_email_notification_enabled:
              values.is_external_data_source_syncing_status_email_notification_enabled,
            is_processor_status_email_notification_enabled:
              values.is_processor_status_email_notification_enabled,
          },
        );
      } else {
        const result = await createProject(
          values.projectName,
          org?.id ?? '',
          ws?.workspace.id ?? '',
          values.projectFile as File,
          agentsArray,
          {
            is_external_data_source_syncing_status_email_notification_enabled:
              values.is_external_data_source_syncing_status_email_notification_enabled,
            is_processor_status_email_notification_enabled:
              values.is_processor_status_email_notification_enabled,
          },
          // selectedAgents,
        );
        if (!result?.succeeded) {
          toast('Error creating new project:', {
            description: result?.message,
            duration: 5000,
          });
        }
      }

      setIsLoading(false);
      setOpen(false);
      setSelectedAgents({});
    }
  }

  useEffect(() => {
    if (promtColumns?.isSuccess) {
      if (promtColumns?.data?.length > 0) {
        $promptColumns.set(promtColumns?.data);
      }
    }
  }, []);

  useEffect(() => {
    form.setValue('agents', selectedAgents);
  }, [selectedAgents]);

  return (
    <Sheet
      open={open}
      onOpenChange={o => {
        form.reset();
        setOpen(o);
        setSelectedAgents({});
        setSourceType('csv');
      }}
    >
      <SheetContent className="overflow-y-auto sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Create new Project</SheetTitle>
          <SheetDescription>
            Create a project for each file that you want to process.
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-4 space-y-8"
          >
            <FormField
              control={form.control}
              name="projectName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project name</FormLabel>
                  <FormControl>
                    <Input placeholder="Q1 Call Records" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter a project name like "Q1 Call Records" to help identify
                    this file.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Tabs defaultValue="csv" className="">
              <TabsList>
                <TabsTrigger
                  onClick={() => {
                    form.setValue('sourceType', 'csv');
                    setSourceType('csv');
                  }}
                  value="csv"
                >
                  CSV File
                </TabsTrigger>
                <TabsTrigger
                  onClick={() => {
                    form.setValue('sourceType', 'zendesk');
                    setSourceType('zendesk');
                  }}
                  value="zendesk"
                >
                  Zendesk
                </TabsTrigger>

                <TabsTrigger
                  onClick={() => {
                    form.setValue('sourceType', 'aircall');
                    setSourceType('aircall');
                  }}
                  value="aircall"
                >
                  Aircall
                </TabsTrigger>
                <TabsTrigger
                  onClick={() => {
                    form.setValue('sourceType', 'audiofile');
                    setSourceType('audiofile');
                  }}
                  value="audiofile"
                >
                  Audio File
                </TabsTrigger>
              </TabsList>
              <TabsContent value="csv">
                <FormField
                  control={form.control}
                  name="projectFile"
                  render={({ field: { onChange }, ...field }) => (
                    <FormItem>
                      <FormLabel>Input CSV File</FormLabel>
                      <FormControl>
                        <InputWithButton
                          id="projectFile"
                          type="file"
                          accept="text/csv"
                          multiple={false}
                          {...field}
                          onChange={event =>
                            onChange(
                              event.target.files ? event.target.files[0] : null,
                            )
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        The uploaded file should have a header row and the
                        columns: <strong>id</strong>, <strong>context</strong>{' '}
                        and <strong>date</strong>. If you want to persist
                        additional data, please use the headers:{' '}
                        <strong>meta1</strong>, <strong>meta2</strong>,{' '}
                        <strong>meta3</strong>.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>
              <TabsContent value="audiofile">
                <FormField
                  control={form.control}
                  name="projectFile"
                  render={({ field: { onChange }, ...field }) => (
                    <FormItem>
                      <FormLabel>Input Audio File</FormLabel>
                      <FormControl>
                        <InputWithButton
                          type="file"
                          accept="audio/mpeg, audio/wav, audio/x-m4a" // Accept MP3, WAV, M4A files
                          multiple={true}
                          {...field}
                          onChange={event => {
                            const files = event.target.files
                              ? Array.from(event.target.files)
                              : [];
                            if (files.length > 100) {
                              // Show a message or handle the error as needed
                              alert('You can upload a maximum of 100 files.');
                              onChange(null); // Reset the input if the limit is exceeded
                            } else {
                              onChange(files.length > 0 ? files : null);
                            }
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        The uploaded audio file should be following fields:{' '}
                        <strong>MP3</strong> or <strong>WAV</strong> or{' '}
                        <strong>M4A</strong>.
                        {/* If
                        you want to persist additional data, please use the
                        fields: <strong>meta1</strong>, <strong>meta2</strong>,{' '}
                        <strong>meta3</strong>. */}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>
              <TabsContent value="zendesk">
                {activeIntegrations?.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : activeIntegrations?.data &&
                  activeIntegrations?.data?.length > 0 &&
                  activeIntegrations?.data?.find(
                    dat => dat?.integration?.type === 'Zendesk',
                  ) !== undefined ? (
                  <div className="">
                    <p className="mb-1 mt-2 text-sm font-medium">
                      Select Date range
                    </p>
                    <div className={cn('grid gap-2')}>
                      <Popover modal>
                        <PopoverTrigger asChild>
                          <Button
                            id="date-zendesk"
                            variant={'outline'}
                            className={cn(
                              ' justify-start text-left font-normal',
                              !date && 'text-muted-foreground',
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {date?.from ? (
                              date.to ? (
                                <>
                                  {format(date.from, 'LLL dd, y')} -{' '}
                                  {format(date.to, 'LLL dd, y')}
                                </>
                              ) : (
                                format(date.from, 'LLL dd, y')
                              )
                            ) : (
                              <span>Pick a date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="flex w-auto p-0"
                          align="start"
                        >
                          <div className="flex w-[200px] flex-col gap-2 pb-4 pl-4 pr-1 pt-4">
                            <Button
                              variant="outline"
                              onClick={() => {
                                // set the date to last 7 days using the helper function
                                const from = addDays(new Date(), -7);
                                const to = new Date();
                                setDate({ from, to });
                              }}
                            >
                              Last 7 days
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                const from = addDays(new Date(), -30);
                                const to = new Date();
                                setDate({ from, to });
                              }}
                            >
                              Last 30 days
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                const from = startOfWeek(new Date());
                                const to = new Date();
                                setDate({ from, to });
                              }}
                            >
                              This week
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                const from = startOfMonth(new Date());
                                const to = new Date();
                                setDate({ from, to });
                              }}
                            >
                              This month
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                const from = startOfQuarter(new Date());
                                const to = new Date();
                                setDate({ from, to });
                              }}
                            >
                              This quarter
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                const from = startOfYear(new Date());
                                const to = new Date();
                                setDate({ from, to });
                              }}
                            >
                              This year
                            </Button>
                          </div>
                          <div className="">
                            <Calendar
                              initialFocus
                              mode="range"
                              defaultMonth={date?.to || new Date()}
                              selected={date}
                              onSelect={setDate}
                              numberOfMonths={2}
                            />
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm font-medium">
                      You need to connect your Zendesk account to create a
                      project.
                    </p>
                    <Button
                      className="mt-2"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        openPage($router, 'settings');
                      }}
                    >
                      Open Settings
                    </Button>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="aircall">
                {activeIntegrations?.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : activeIntegrations?.data &&
                  activeIntegrations?.data?.length > 0 &&
                  activeIntegrations?.data?.find(
                    dat => dat?.integration?.type === 'Aircall',
                  ) !== undefined ? (
                  <div className="">
                    <p className="mb-1 mt-2 text-sm font-medium">
                      Select Date range
                    </p>
                    <div className={cn('grid gap-2')}>
                      <Popover modal>
                        <PopoverTrigger asChild>
                          <Button
                            id="date-aircall"
                            variant={'outline'}
                            className={cn(
                              ' justify-start text-left font-normal',
                              !date && 'text-muted-foreground',
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {date?.from ? (
                              date.to ? (
                                <>
                                  {format(date.from, 'LLL dd, y')} -{' '}
                                  {format(date.to, 'LLL dd, y')}
                                </>
                              ) : (
                                format(date.from, 'LLL dd, y')
                              )
                            ) : (
                              <span>Pick a date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="flex w-auto p-0"
                          align="start"
                        >
                          <div className="flex w-[200px] flex-col gap-2 pb-4 pl-4 pr-1 pt-4">
                            <Button
                              variant="outline"
                              onClick={() => {
                                // set the date to last 7 days using the helper function
                                const from = addDays(new Date(), -7);
                                const to = new Date();
                                setDate({ from, to });
                              }}
                            >
                              Last 7 days
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                const from = addDays(new Date(), -30);
                                const to = new Date();
                                setDate({ from, to });
                              }}
                            >
                              Last 30 days
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                const from = startOfWeek(new Date());
                                const to = new Date();
                                setDate({ from, to });
                              }}
                            >
                              This week
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                const from = startOfMonth(new Date());
                                const to = new Date();
                                setDate({ from, to });
                              }}
                            >
                              This month
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                const from = startOfQuarter(new Date());
                                const to = new Date();
                                setDate({ from, to });
                              }}
                            >
                              This quarter
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                const from = startOfYear(new Date());
                                const to = new Date();
                                setDate({ from, to });
                              }}
                            >
                              This year
                            </Button>
                          </div>
                          <div className="">
                            <Calendar
                              initialFocus
                              mode="range"
                              defaultMonth={date?.to || new Date()}
                              selected={date}
                              onSelect={setDate}
                              numberOfMonths={2}
                            />
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm font-medium">
                      You need to connect your Aircall account to create a
                      project.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      color="primary"
                      className="mt-2"
                      onClick={() => {
                        openPage($router, 'settings');
                      }}
                    >
                      Open Settings
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <div className="flex flex-col space-x-2">
              <label
                htmlFor="terms"
                className="-mt-4 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Project preferences
              </label>

              {(sourceType === 'zendesk' || sourceType === 'aircall') && (
                <FormField
                  control={form.control}
                  name="is_external_data_source_syncing_status_email_notification_enabled"
                  render={({ field }) => (
                    <FormItem className="mt-4 flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>
                          Notify when {sourceType} data is synced
                        </FormLabel>
                      </div>
                    </FormItem>
                  )}
                />
              )}

              {(sourceType === 'zendesk' || sourceType === 'aircall') && (
                <FormField
                  control={form.control}
                  name="auto_run_analyzer_after_external_data_source_syncing_completed"
                  render={({ field }) => (
                    <FormItem className="mt-4 flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>

                          Auto process records after {sourceType} data is synced
                        </FormLabel>
                      </div>
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="is_processor_status_email_notification_enabled"
                render={({ field }) => (
                  <FormItem className="mt-4 flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        Notify when data processing is completed
                      </FormLabel>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="agents"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Select Agents</FormLabel>
                  <FormControl>
                    <AgentsSelector
                      promptColumns={promtColumns?.data}
                      agents={selectedAgents}
                      setAgents={setSelectedAgents}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SheetFooter>
              <SheetClose asChild>
                <Button variant="outline">Cancel</Button>
              </SheetClose>
              <Button
                type="submit"
                disabled={isLoading || promtColumns?.isLoading}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
